import { useState, type ReactNode } from 'react'
import { AnimatePresence } from 'framer-motion'
import type { Household } from '../lib/supabase'
import {
  CONVERSION_NOTE, DEFAULT_PLAN, FREE_ITEM_LIMIT, LAUNCH_OFFER, MAX_MEMBERS, PLAN_NAME, PLANS, PLUS_FEATURES,
  amountsFor, launchPrice, planNote, planPrice, taxNote, yearlyPrice, type PlanAmounts, type PlanId,
} from '../lib/pricing'
import { householdAccess, trialDaysLeft, type PlusReason } from '../lib/billing'
import { currencyLabel } from '../lib/currency'
import { userLocale } from '../lib/locale'
import type { Prices } from '../hooks/usePrices'
import type { EmbeddedSession } from '../hooks/useBilling'
import { useLayout } from '../hooks/useLayout'
import { SheetPanel } from './SheetPanel'
import { EmbeddedCheckout } from './EmbeddedCheckout'
import { Mark } from './Wordmark'
import { CheckIcon, ChevronIcon, LockIcon } from './icons'

interface Props {
  open: boolean
  household: Household
  prices: Prices
  loading: boolean
  error: string | null
  /** The currency is the one displayed — Checkout is told to charge in it. */
  onCheckout: (plan: PlanId, currency: string) => void
  /** Checkout's embedded form, once the plan is chosen; null while choosing. */
  session?: EmbeddedSession | null
  /** Back from the form to the plans. */
  onBack?: () => void
  /** The form says it's paid: the sheet turns into the confirmation. */
  onPaid?: () => void
  /** Paid in this sheet — shown until it is closed, whatever the webhook does meanwhile. */
  paid?: boolean
  /** What the tap that opened the sheet was after; the sheet leads with it. */
  reason?: PlusReason | null
  onClose: () => void
}

/** Headline and line for each reason, at the yearly price in the viewer's currency. */
function reasonCopy(reason: PlusReason, year: string): { title: string; body: string } {
  switch (reason) {
    case 'list_full': return {
      title: 'Your list is full',
      body: `Free holds ${FREE_ITEM_LIMIT} items at a time. Plus gives you unlimited items for ${year} a year.`,
    }
    case 'recipes': return {
      title: 'Recipes are part of Plus',
      body: `Import any recipe link, write your own and put the ingredients on the list in one tap, for ${year} a year. Your saved recipes stay readable on Free.`,
    }
    case 'planning': return {
      title: 'Meal planning is part of Plus',
      body: `Plan the week, save week templates and add every meal’s ingredients at once, for ${year} a year.`,
    }
    case 'memory': return {
      title: 'Matlist remembers what you buy',
      body: `From before and Running low bring back what you’ve bought, one tap at a time. Keep them with Plus for ${year} a year.`,
    }
    case 'store': return {
      title: 'Matlist learned your store',
      body: `Keep the list in your walking order with Plus for ${year} a year.`,
    }
    case 'stats': return {
      title: 'Stats are part of Plus',
      body: `Your trips, time in store, spending and staples, for ${year} a year.`,
    }
    case 'watch': return {
      title: 'Your list, on your wrist',
      body: `Tick things off from your Garmin with Plus for ${year} a year.`,
    }
  }
}

const FREE_KEEPS = `Free keeps the shared list (up to ${FREE_ITEM_LIMIT} items at a time), shopping mode and your saved recipes.`

// The one place a household is asked to pay, in three steps: the plan, then
// Stripe's embedded Checkout form in the same sheet (never leaving the app),
// then a confirmation. Opened by App.tsx from Settings, from a plan caption, or
// whenever a tap reaches for a Plus feature on Free (with the reason, which
// leads) — and closed by it again the moment the Stripe webhook flips
// subscription_status (via the households realtime subscription in
// useHousehold).
//
// The brand sits in an ink band, ink in both themes like the launch splash: the
// lime mark, the wordmark and PLUS. On the phone the band tops a single column;
// on desktop and iPad the sheet is the wide dialog, the band a full-height
// panel beside the plans (and later beside Stripe's form, which never shows a
// merchant logo of its own).
export function SubscribeSheet({ open, household, prices, loading, error, onCheckout, session = null, onBack, onPaid, paid = false, reason = null, onClose }: Props) {
  const desktop = useLayout() === 'desktop'
  const [plan, setPlan] = useState<PlanId>(DEFAULT_PLAN)
  const [pickingCurrency, setPickingCurrency] = useState(false)
  const access = householdAccess(household)
  const daysLeft = trialDaysLeft(household)
  // Was paid at some point — "resubscribe", and the old billing currency wins.
  const lapsed = household.subscription_status !== 'trialing'
  const locale = userLocale()
  const { table, currency, available } = prices
  const amounts = amountsFor(table, currency)
  // The base is shown when nothing better matched the viewer — Checkout may
  // then convert on its own, which the copy below says.
  const converted = currency === table.base && available.length > 1 && !prices.usingFallback
  const year = yearlyPrice(amounts, currency, locale)

  const led = reason && access === 'free' ? reasonCopy(reason, year) : null
  const headline = led?.title ?? (access === 'trialing'
    ? `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left in your trial`
    : access === 'free'
      ? `${household.name} is on Free`
      : `${PLAN_NAME} for ${household.name}`)
  const body = led?.body ?? (access === 'trialing'
    ? `After it, ${household.name} carries on with Free, or keeps everything with ${PLAN_NAME}. If you get ${PLAN_NAME} now, billing starts today.`
    : access === 'free'
      ? `The list keeps working with up to ${FREE_ITEM_LIMIT} items at a time, and nothing has been deleted. ${PLAN_NAME} brings back everything else, for up to ${MAX_MEMBERS} people.`
      : `Everything, for the whole household, up to ${MAX_MEMBERS} people.`)

  const chosen = PLANS.find(p => p.id === plan)!
  const step = paid ? 'paid' : session ? 'pay' : 'choose'

  // On the phone's pay step the band shrinks to one line, so Stripe's form
  // (which can't be shortened from here) starts as high as it can.
  const compact = step === 'pay' && !desktop

  const band = (
    <section className={`ml-plus__band${compact ? ' ml-plus__band--compact' : ''}`} data-testid="plus-band">
      <div className="ml-plus__brand">
        <span role="img" aria-label="Matlist" className="ml-plus__lockup">
          <Mark height={15} color="var(--ml-band-lime)" cropped />
          <span className="matlist-wordmark ml-plus__word" aria-hidden="true">Matlist</span>
        </span>
        <span className="ml-plus__tag">{PLAN_NAME}</span>
        {step === 'pay' && (
          <button type="button" className="ml-plus__back" onClick={onBack}>
            <ChevronIcon size={16} className="ml-plus__back-icon" /> Back to plans
          </button>
        )}
      </div>

      {step === 'choose' && (<>
        <h2 className="ml-display ml-plus__title">{headline}</h2>
        <p className="ml-plus__lede">{body}</p>
        {desktop && <Included />}
      </>)}

      {step === 'pay' && (compact ? (
        <p className="ml-plus__oneline">
          <strong>{PLAN_NAME}, {chosen.label.toLowerCase()}</strong> {summaryLine(plan, amounts, currency, locale)}
        </p>
      ) : (
        <div className="ml-plus__summary">
          <p className="ml-plus__summary-name">{PLAN_NAME}, {chosen.label.toLowerCase()}</p>
          <PriceSummary plan={plan} amounts={amounts} currency={currency} locale={locale} />
          {desktop && <Included />}
        </div>
      ))}

      {step === 'paid' && (<>
        <span className="ml-plus__done" aria-hidden="true"><CheckIcon size={30} /></span>
        <h2 className="ml-display ml-plus__title">Welcome to {PLAN_NAME}</h2>
        <p className="ml-plus__lede">
          {access === 'free'
            ? `${household.name} has everything again, for everyone on it.`
            : `${household.name} carries on without a break, for everyone on it.`}
          {' '}Your receipt is on its way to your email.
        </p>
      </>)}
    </section>
  )

  return (
    <AnimatePresence>
      {open && (
        <SheetPanel onClose={onClose} maxHeight="94dvh" layout="scroll" role="dialog" wide={desktop}
          ariaLabel={`${PLAN_NAME} for ${household.name}`}>
          <div data-testid="plus-sheet" className={`ml-plus${desktop ? ' ml-plus--desktop' : ''}`}>
            {band}
            <div className="ml-plus__body">
              {step === 'choose' && (<>
                <div role="radiogroup" aria-label="Plan" className="ml-options">
                  {PLANS.map(p => (
                    <PlanRow key={p.id} plan={p.id} label={p.label} selected={p.id === plan}
                      amounts={amounts} currency={currency} locale={locale} onSelect={() => setPlan(p.id)} />
                  ))}
                </div>

                {!desktop && <Included />}

                <button type="button" className="ml-btn ml-btn--primary ml-btn--wide ml-plus__cta"
                  onClick={() => onCheckout(plan, currency)} disabled={loading}>
                  {loading ? 'One moment…' : lapsed ? 'Resubscribe' : `Get ${PLAN_NAME}`}
                </button>

                <p className="ml-plus__trust">
                  <LockIcon size={14} />
                  <span>
                    Secure payment by Stripe. {taxNote(amounts)}
                    {converted && <> {CONVERSION_NOTE}</>}
                    {lapsed && <> Checkout shows the final price in your existing billing currency.</>}
                  </span>
                </p>

                {/* Which currency the amounts are in, and a way to change it
                    — the clock/language guess is usually right, but a Swede on
                    an English phone abroad shouldn't be stuck paying in the wrong one. */}
                {available.length > 1 && (
                  <p className="ml-plus__currency">
                    {pickingCurrency ? (
                      <label>
                        Prices in{' '}
                        <select aria-label="Currency" value={currency}
                          onChange={e => { prices.setCurrency(e.target.value); setPickingCurrency(false) }}>
                          {available.map(c => <option key={c} value={c}>{currencyLabel(c)}</option>)}
                        </select>
                      </label>
                    ) : (<>
                      Prices in {currencyLabel(currency)}.{' '}
                      <button type="button" className="ml-text-btn ml-plus__change" onClick={() => setPickingCurrency(true)}>Change</button>
                    </>)}
                  </p>
                )}

                {error && <p className="ml-plus__error" role="alert">{error}</p>}

                <button type="button" className="ml-plus__later" onClick={onClose}>Not now</button>
              </>)}

              {step === 'pay' && session && (
                <EmbeddedCheckout session={session} onComplete={() => onPaid?.()} />
              )}

              {step === 'paid' && (
                <button type="button" className="ml-btn ml-btn--primary ml-btn--wide" onClick={onClose}>Done</button>
              )}
            </div>
          </div>
        </SheetPanel>
      )}
    </AnimatePresence>
  )
}

/** What Plus adds, and in one sentence what Free keeps. */
function Included() {
  return (
    <div className="ml-plus__included">
      <p id="ml-plus-included" className="ml-label ml-plus__included-label">What’s in {PLAN_NAME}</p>
      <ul aria-labelledby="ml-plus-included" className="ml-plus__list">
        {PLUS_FEATURES.map(f => (
          <li key={f}><span className="ml-plus__tick" aria-hidden="true"><CheckIcon size={14} /></span>{f}</li>
        ))}
      </ul>
      <p className="ml-plus__free">{FREE_KEEPS}</p>
    </div>
  )
}

/** One plan as a radio: name, its note, the price — and on yearly, the founding offer. */
function PlanRow({ plan, label, selected, amounts, currency, locale, onSelect }: {
  plan: PlanId; label: string; selected: boolean
  amounts: PlanAmounts; currency: string; locale: string; onSelect: () => void
}) {
  const annual = plan === 'annual'
  const offer = annual && LAUNCH_OFFER
  const price = planPrice(plan, amounts, currency, locale)
  return (
    <button type="button" role="radio" aria-checked={selected} onClick={onSelect}
      className={`ml-option${selected ? ' is-selected' : ''}`}>
      <span className="ml-option__radio" aria-hidden="true" />
      <span className="ml-option__main">
        <span className="ml-option__name">
          {label}
          {annual && <span className="ml-option__best">Best value</span>}
        </span>
        <span className="ml-option__note">{annual ? planNote('annual', amounts, currency, locale) : 'Billed every month'}</span>
      </span>
      <span className="ml-option__price">
        {offer ? (<>
          <s className="ml-option__was" data-testid="full-price">{price}</s>
          <span className="ml-option__amount ml-num">{launchPrice(amounts, currency, locale)}</span>
          <span className="ml-option__per">first year</span>
        </>) : (<>
          <span className="ml-option__amount ml-num">{price}</span>
          <span className="ml-option__per">{annual ? 'a year' : 'a month'}</span>
        </>)}
      </span>
      {offer && (
        <span className="ml-option__offer" data-testid="promo-note">
          <span className="ml-option__flag">−{offer.percentOff}% founding offer</span>
          <span>Then {price} a year. Applied at checkout.</span>
        </span>
      )}
    </button>
  )
}

/** The chosen plan's price in one sentence, for the phone's compact band. */
function summaryLine(plan: PlanId, amounts: PlanAmounts, currency: string, locale: string): string {
  const price = planPrice(plan, amounts, currency, locale)
  if (plan === 'annual' && LAUNCH_OFFER) return `${launchPrice(amounts, currency, locale)} the first year, then ${price} a year.`
  return `${price} ${plan === 'annual' ? 'a year' : 'a month'}.`
}

/** The chosen plan's price on the pay step: the founding first year when it applies. */
function PriceSummary({ plan, amounts, currency, locale }: {
  plan: PlanId; amounts: PlanAmounts; currency: string; locale: string
}): ReactNode {
  const price = planPrice(plan, amounts, currency, locale)
  if (plan === 'annual' && LAUNCH_OFFER) {
    return (
      <p className="ml-plus__summary-price">
        <span className="ml-plus__summary-amount ml-num">{launchPrice(amounts, currency, locale)}</span>
        <span>the first year, then {price} a year.</span>
        <span className="ml-plus__summary-note">{taxNote(amounts)}</span>
      </p>
    )
  }
  return (
    <p className="ml-plus__summary-price">
      <span className="ml-plus__summary-amount ml-num">{price}</span>
      <span>{plan === 'annual' ? 'a year' : 'a month'}.</span>
      <span className="ml-plus__summary-note">{taxNote(amounts)}</span>
    </p>
  )
}
